import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, inject } from '@angular/core';
import type SdCheckbox from '@solid-design-system/components/dist-versioned/components/checkbox/checkbox';
import { Subject } from 'rxjs';

@Directive({ selector: '[wctSdCheckboxGroupModel]', standalone: true })
export class SdCheckboxGroupModelDirective implements OnInit, OnDestroy {
  @Input({ alias: 'wctSdCheckboxGroupModel', required: true }) public set value(value: string[]) {
    if (value === this.latestValue) {
      return;
    }

    this.checkedBoxes.clear();
    if (value) {
      for (const element of value) {
        this.checkedBoxes.add(element);
      }
    }
  }

  public get value(): string[] {
    return this.latestValue;
  }

  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('wctSdCheckboxGroupModelChange') public readonly update: Subject<string[]> = new EventEmitter();

  private latestValue: string[] = [];

  private readonly checkedBoxes: Set<string> = new Set();
  private readonly zone = inject(NgZone);
  private readonly elementRef: ElementRef<SdCheckbox> = inject(ElementRef);

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.elementRef.nativeElement.addEventListener('sd-change', this.handleSdChange);
      this.elementRef.nativeElement.addEventListener('sd-input', this.handleSdChange);
    });
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      this.elementRef.nativeElement.removeEventListener('sd-change', this.handleSdChange);
      this.elementRef.nativeElement.removeEventListener('sd-input', this.handleSdChange);
    });
    this.update.complete();
  }

  protected handleSdChange = async (event: Event) => {
    const nativeElement = event.target as SdCheckbox;

    if (nativeElement.updateComplete) {
      await nativeElement.updateComplete;
    }

    this.zone.run(() => {
      const checked: boolean = nativeElement.getAttribute('checked') !== null;

      if (checked) {
        this.checkedBoxes.add(nativeElement.value);
      } else {
        this.checkedBoxes.delete(nativeElement.value);
      }

      this.latestValue = [...this.checkedBoxes.values()];

      this.update.next(this.latestValue);
    });
  };
}
